<template>
  <modal :show="show" size="lg" footerClasses="d-none">
    <validation-observer v-slot="{ invalid }" class="form mx-auto">
      <b-form v-loading="formProcessing" @submit.prevent="handleSubmit">
        <slide-x-left-transition group>
          <div v-if="'user-info' === activeTab" key="user-info">
            <h4 class="mb-4">Add User</h4>

            <b-row>
              <b-col>
                <div class="mb-3">
                  <p class="mb-2">Billing Contact</p>
                  <base-switch
                     id="is-billing-switch"
                     onText=""
                     offText=""
                    :value="formData.billing_contact === 'Active'"
                    @input="val => formData.billing_contact = val ? 'Active' : 'InActive'"
                  ></base-switch>
                </div>
              </b-col>
              <b-col>
                <div v-if="createSuperuser" class="mb-3">
                  <p class="mb-2">Is Superuser?</p>
                  <base-switch id="is-superuser-switch" v-model="formData.isSuperuser"></base-switch>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <CompaniesDropdown
                  ref="companiesDropdownRef"
                  v-model="formData.company"
                  @input="getDefaultReport"
                  @error:noOptions="
                    () =>
                      $refs.formRef.setErrors({
                        company: ['No companies found. Please create a company first.'],
                      })
                  "
                  :rules="formData.isSuperuser ? null : 'required'"
                  :user-company-as-option="!!!currentUser.is_superuser"
                  :disabled="!!forCompany"
                />
              </b-col>
              <b-col>
                <UserTypesDropdown v-model="formData.type" />
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <base-input
                  v-model="formData.display_name"
                  rules="required"
                  name="Display Name"
                  label="Display Name"
                ></base-input>
              </b-col>
              <b-col>
                <base-input
                  v-model="formData.email"
                  rules="required|email"
                  name="Email Address"
                  label="Email Address"
                ></base-input>
              </b-col>
            </b-row>

            <div class="mt-4 d-flex justify-content-between">
              <base-button
                @click="$emit('update:show', false)"
                type="white"
                native-type="button"
                size="md"
                class="form-btn bg-transparent font-weight-normal px-0 shadow-none"
                >Cancel</base-button
              >

              <base-button
                v-if="formData.isSuperuser || 'Corporate Admin' === formData.type"
                :disabled="invalid"
                type="primary"
                native-type="submit"
                size="md"
                class="form-btn font-weight-normal"
                >Save</base-button
              >
              <base-button
                v-else
                @click="activeTab = 'reports-list'"
                :disabled="invalid"
                type="primary"
                native-type="button"
                size="md"
                class="form-btn font-weight-normal"
                >Next</base-button
              >
            </div>
          </div>
          <div v-else key="reports-list">
            <h4 class="mb-2">
              <base-button
                @click="activeTab = 'user-info'"
                type="primary"
                size="sm"
                class="btn-icon-only p-0 mr-2 rounded-circle"
              >
                <i class="fa fa-arrow-left m-0"></i>
              </base-button>
              Assign Report Access
            </h4>
            <p class="mb-4 text-sm">
              Assigning reports is optional at this point. You can manage reports for this
              company at any time.
            </p>

            <ReportsCheckboxList
              v-model="formData.reports"
              @update:reportsFetched="onReportsFetched"
              @update:defaultReport="(value) => (formData.defaultReport = value)"
              :assignee-id="formData.company"
              assignee-type="Corporate Admin"
              :default-report="formData.defaultReport"
              :disabled-reports="[companyDefaultReport]"
            />

            <div class="mt-4 d-flex justify-content-between">
              <base-button
                @click="activeTab = 'user-info'"
                type="white"
                native-type="button"
                size="md"
                class="form-btn bg-transparent font-weight-normal px-0 shadow-none"
                >Return</base-button
              >
              <base-button
                :disabled="invalid"
                type="primary"
                native-type="submit"
                size="md"
                class="form-btn font-weight-normal"
                >Save</base-button
              >
            </div>
          </div>
        </slide-x-left-transition>
      </b-form>
    </validation-observer>

    <template #footer>
      <div class="sr-only"></div>
    </template>
  </modal>
</template>

<script>
import Vue from "vue";
import { defineComponent, reactive, ref, watch } from "@vue/composition-api";
import { useStore } from "vue2-helpers/vuex";
import { SlideXLeftTransition } from "vue2-transitions";

import { useAuth } from "@composables/auth";
import { useData } from "@composables/data";
import Companies from "@services/Companies";
import Members from "@services/Members";
import Reports from "@services/Reports";

import CompaniesDropdown from "@components/CompaniesDropdown";
import UserTypesDropdown from "@components/UserTypesDropdown";
import ReportsCheckboxList from "@components/ReportsCheckboxList";

export default defineComponent({
  name: "AddUserModal",
  components: {
    SlideXLeftTransition,
    CompaniesDropdown,
    UserTypesDropdown,
    ReportsCheckboxList,
  },
  emits: ["update:show", "success"],
  props: {
    show: Boolean,
    createSuperuser: Boolean,
    forCompany: Number,
  },
  setup(props, { emit }) {
    const store = useStore();

    const { currentUser, userRole } = useAuth();
    const { resetData } = useData();

    const initialFormData = {
      defaultReport: null,
      company: props.forCompany || null,
      type: "Employee",
      status: "Active",
      reports: [],
      billing_contact: "InActive",
    };

    const activeTab = ref("user-info");
    const formProcessing = ref(false);
    const companyDefaultReport = ref(null);

    const formData = reactive({
      ...initialFormData,
    });

    const onReportsFetched = (reports) =>
      (formData.reports = reports.map((item) => item.value));

    const getDefaultReport = async () => {
      if (currentUser.value.is_superuser) {
        if (formData.company) {
          const resp = await Companies.readCompany(formData.company);
          const defaultReportId = resp.data.data.data.default_report_id;

          formData.defaultReport = defaultReportId;

          companyDefaultReport.value = defaultReportId;
        } else {
          formData.reports = [];
          formData.defaultReport = null;

          companyDefaultReport.value = null;
        }
      } else if ("Corporate Admin" === userRole.value) {
        formData.defaultReport = currentUser.value.company.default_report_id;

        companyDefaultReport.value = currentUser.value.company.default_report_id;
      }
    };

    const handleSubmit = async () => {
      formProcessing.value = true;

      const memberResp = await Members.createMember(formData, !!formData.isSuperuser);
      const memberObj = memberResp.data.data.data;

      if (!formData.isSuperuser) {
        if ("Employee" === formData.type && formData.reports.length) {
          const reportsResp = await Reports.assignReports(
            formData.reports,
            [memberObj.id],
            formData.company
          );
          const assignedReportsList = reportsResp.data.data.data;

          memberObj.report_count = assignedReportsList.length;
          memberObj.reports = assignedReportsList.map((item) => ({
            report_id: item.report.id,
            report_assigned_member_id: item.id,
          }));

          if (currentUser.value.is_superuser) {
            if (companyDefaultReport.value != formData.defaultReport) {
              const defaultReportObj = assignedReportsList.find(
                (item) => item.report.id == formData.defaultReport
              );

              await Members.setDefaultReport(memberObj.user.id, defaultReportObj.id);
            }
          }

          Vue.prototype.$toasted.show("Reports assigned successfully.");
        }

        store.commit("CREATE_MEMBER", {
          data: memberObj,
        });

        Vue.prototype.$bus.$emit("report-access-user-created", memberObj);
      }

      formProcessing.value = false;
      activeTab.value = "user-info";

      emit("update:show", false);
    };

    watch(
      () => props.show,
      (newShow) => {
        if (newShow) {
          if (props.forCompany) getDefaultReport();
        } else {
          resetData(formData, initialFormData);
        }
      }
    );

    return {
      currentUser,
      activeTab,
      formProcessing,
      companyDefaultReport,
      formData,
      onReportsFetched,
      getDefaultReport,
      handleSubmit,
    };
  },
});
</script>
